import type { ReactNode } from "react";

const R = 18;
const STROKE = 3;
const CIRCUMFERENCE = 2 * Math.PI * R;
// Round caps reach STROKE/2 past each end of a dash, so the visible gap
// between two segments is GAP - STROKE.
const GAP = STROKE + 4;

export interface RingSegment {
  done: boolean;
  /** Dash length along the circle. */
  length: number;
  /** strokeDashoffset that starts this dash at its slot. */
  offset: number;
}

/**
 * One arc per counted step, in checklist order, with a gap between each — the
 * story-ring look, where you can see WHICH steps are done, not just how many.
 * A single step draws a full circle (no gap to show).
 */
export function ringSegments(steps: readonly boolean[]): RingSegment[] {
  const n = steps.length;
  if (n === 0) return [];
  if (n === 1) return [{ done: steps[0] === true, length: CIRCUMFERENCE, offset: 0 }];
  const slot = CIRCUMFERENCE / n;
  return steps.map((done, i) => ({
    done,
    length: slot - GAP,
    offset: -(i * slot + GAP / 2),
  }));
}

/**
 * A segmented progress ring drawn AROUND its child (the avatar). The wrapper
 * keeps the avatar's own h-8 w-8 footprint and the ring overflows it, so the
 * top bar gains no width (already tight at 360px, DEV-862).
 */
export function ProgressRing({
  steps,
  label,
  children,
}: {
  /** Done-ness of each counted (non-locked) step, in checklist order. */
  steps: readonly boolean[];
  label: string;
  children: ReactNode;
}) {
  return (
    <span data-testid="progress-ring" className="relative inline-flex h-8 w-8 items-center justify-center">
      <svg
        className="pointer-events-none absolute -left-1 -top-1 h-10 w-10 -rotate-90 overflow-visible"
        viewBox="0 0 40 40"
        role="img"
        aria-label={label}
      >
        {ringSegments(steps).map((s, i) => (
          <circle
            key={i}
            data-testid="progress-ring-segment"
            data-done={s.done}
            cx="20"
            cy="20"
            r={R}
            fill="none"
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={`${s.length} ${CIRCUMFERENCE - s.length}`}
            strokeDashoffset={s.offset}
            className={`transition-colors duration-500 ${s.done ? "stroke-brand-500" : "stroke-border-default"}`}
          />
        ))}
      </svg>
      {children}
    </span>
  );
}
